import Badge from '@/components/Badge'
import Breadcrumbs from '@/components/Breadcrumbs'
import DynamicContent from '@/components/DynamicContent'
import PageHeader from '@/components/PageHeader/PageHeader'
import AppLayout from '@/layouts/AppLayout'
import ContentLayout from '@/layouts/ContentLayout'
import LayoutService from '@/services/LayoutService'
import SubjectService from '@/services/SubjectService'
import UserService from '@/services/UserService'
import deepCopy from '@/utils/deepCopy'
import filterMemberContent from '@/utils/filterMemberContent'
import getSectionData from '@/utils/getSectionData'
import resolveSidebarBlocks from '@/utils/resolveSidebarBlocks'
import { StarIcon } from '@heroicons/react/24/solid'
import cn from 'classnames'
import { useRouter } from 'next/router'
import { useEffect, useState } from 'react'
import styles from './subjects-id.module.scss'

function Page({ subject, layoutData }) {
  const router = useRouter()
  const breadcrumbs = [{ title: 'Onderwerpen', uri: '/onderwerpen' }, { title: subject.title }]

  const originalSubject = deepCopy(subject)
  const isMemberSubject = !!subject?.membersAcf?.membersOnly

  const [isAuthenticated, setIsAuthenticated] = useState(false)

  const [filteredContent, setFilteredContent] = useState(
    originalSubject.dynamicAcf.dynamicContent.filter((block) => !block?.membersOnly)
  )

  const acfMain = filteredContent?.dynamicAcf?.dynamicContent

  const filteredSidebarBlocks = isMemberSubject && !isAuthenticated
    ? subject.sidebarAcf.sidebarBlocks.filter((block) => block.contentType === 'share')
    : subject.sidebarAcf.sidebarBlocks;

  const acfAside = resolveSidebarBlocks(
    { ...filteredContent, sidebarAcf: { ...filteredContent.sidebarAcf, sidebarBlocks: filteredSidebarBlocks } },
    subject
  );

  useEffect(() => {
    const Cookie = document.cookie
    const userShowResponse = async () => await UserService().show({ Cookie })
    userShowResponse().then((res) => {
      if (res.status !== 200) return setFilteredContent(filterMemberContent(originalSubject))
      setIsAuthenticated(true)
      setFilteredContent(subject)
    })
  }, [router.asPath])

  return (
    <AppLayout
      page={subject}
      data={layoutData}
    >
      <PageHeader data={subject.sections} />

      <ContentLayout className={cn(styles['root'])}>
        <ContentLayout.Main>
          <Breadcrumbs
            breadcrumbs={breadcrumbs}
            className="pb-12"
          />

          <ContentLayout.Main.IntroHeader>
            <ContentLayout.Main.IntroHeader.Content className={cn(styles['introheader__content'])}>
              <h1>
                <span>{subject.title}</span>

                {subject?.membersAcf?.membersOnly && (
                  <Badge className={cn('badge--member')}>
                    <StarIcon />
                    Leden
                  </Badge>
                )}
              </h1>
            </ContentLayout.Main.IntroHeader.Content>
          </ContentLayout.Main.IntroHeader>

          <DynamicContent
            data={acfMain}
            withWall={isMemberSubject && !isAuthenticated}
          />
        </ContentLayout.Main>

        <ContentLayout.Aside className={cn(styles['root__aside'])}>
          <DynamicContent data={acfAside} />
        </ContentLayout.Aside>
      </ContentLayout>
    </AppLayout>
  )
}

export async function getStaticPaths() {
  let subjects = []
  const BASE_URL = `${process.env.NEXT_PUBLIC_CMS_REST_ENDPOINT}/wp/v2/subject?per_page=100&_fields=slug`
  const { headers } = await fetch(BASE_URL)
  const pages = headers.get('x-wp-totalpages')

  for (let page = 1; page <= pages; page++) {
    const response = await fetch(`${BASE_URL}&page=${page}`)
    subjects = [...subjects, ...(await response.json())]
  }

  return {
    paths: subjects.map((subject) => ({ params: { id: subject.slug } })),
    fallback: 'blocking',
  }
}

export async function getStaticProps({ params, preview, previewData }) {
  const { id } = params
  const isId = Number.isInteger(Number(id))

  try {
    const { data } = await SubjectService().show(id, isId ? 'DATABASE_ID' : 'URI', previewData)
    const { data: layoutData } = await LayoutService().index()
    let subject = data.subject

    if (!subject) return { notFound: true }

    if (preview) {
      subject = { ...subject, ...data.subject.revisions.nodes[0] }
    }

    subject.sections = getSectionData(data.page.sections)

    return {
      props: { subject, layoutData },
      revalidate: 10,
    }
  } catch (error) {
    throw new Error(error)
  }
}

export default Page
